import {
  ShaderMaterial,
  AdditiveBlending,
  NormalBlending,
  Color,
  DoubleSide
} from 'three';
import { noiseGLSL } from '../shaders/lib/noise.glsl.js';
import { sharedUniforms } from '../core/FrameUniforms.js';

/**
 * A small visual language shared by the five construct abilities.
 *
 * Sharing the shader plumbing does not make the abilities share a silhouette:
 * STYLE selects how a real piece of geometry is shaded, while GLYPH selects a
 * completely different ground-space contract. The abilities still own their
 * timing, topology and spatial behaviour.
 */
export const ConstructStyle = Object.freeze({
  PRISM: 0,
  VOID: 1,
  BLADE: 2,
  WATER: 3,
  SOLAR: 4,
  ENERGY: 5
});

export const ConstructGlyph = Object.freeze({
  PRISM: 0,
  VOID: 1,
  SLASH: 2,
  WATER: 3,
  SOLAR: 4
});

const SURFACE_VERTEX = /* glsl */ `
  varying vec2 vUv;
  varying vec3 vLocal;
  varying vec3 vNormalW;
  varying vec3 vViewW;
  varying vec3 vWorld;

  void main() {
    vUv = uv;
    vLocal = position;
    vec4 localPosition = vec4(position, 1.0);
    vec3 localNormal = normal;
    #ifdef USE_INSTANCING
      localPosition = instanceMatrix * localPosition;
      // Inverse-transpose of a 3x3 instance matrix, expanded explicitly so
      // non-uniformly scaled plates, walls and thorns keep correct normals.
      mat3 instanceNormalMatrix = mat3(instanceMatrix);
      localNormal /= max(vec3(
        dot(instanceNormalMatrix[0], instanceNormalMatrix[0]),
        dot(instanceNormalMatrix[1], instanceNormalMatrix[1]),
        dot(instanceNormalMatrix[2], instanceNormalMatrix[2])
      ), vec3(1e-8));
      localNormal = instanceNormalMatrix * localNormal;
    #endif
    vec4 world = modelMatrix * localPosition;
    vWorld = world.xyz;
    vNormalW = normalize(mat3(modelMatrix) * localNormal);
    vViewW = normalize(cameraPosition - world.xyz);
    gl_Position = projectionMatrix * viewMatrix * world;
  }
`;

const SURFACE_FRAGMENT = /* glsl */ `
  uniform float uTime;
  uniform float uReveal;
  uniform float uFade;
  uniform float uPulse;
  uniform float uIntensity;
  uniform float uFlow;
  uniform float uSeed;
  uniform float uOpacity;
  uniform float uGlobalGlow;
  uniform vec3 uColorA;
  uniform vec3 uColorB;
  uniform vec3 uColorC;

  varying vec2 vUv;
  varying vec3 vLocal;
  varying vec3 vNormalW;
  varying vec3 vViewW;
  varying vec3 vWorld;

  ${noiseGLSL}

  void main() {
    float fresnel = pow(1.0 - abs(dot(normalize(vNormalW), normalize(vViewW))), 2.2);
    float along = clamp(vUv.y, 0.0, 1.0);
    float noise = 0.5 + 0.5 * snoise(vWorld * 1.35 + vec3(uSeed, uTime * uFlow, -uSeed));
    float alpha = 1.0;
    vec3 color = uColorA;

    #if STYLE == 0
      float spectrum = 0.5 + 0.5 * sin(
        dot(vNormalW, vec3(3.1, 5.3, 7.7)) + uTime * 1.3 + uSeed * 4.0
      );
      float facet = pow(abs(dot(vNormalW, normalize(vec3(0.4, 0.8, 0.3)))), 0.7);
      color = mix(uColorA, uColorB, spectrum);
      color = mix(color, uColorC, fresnel * 0.7 + facet * 0.2);
      alpha = 0.22 + fresnel * 0.72 + noise * 0.12;
    #elif STYLE == 1
      float throat = smoothstep(0.15, 0.95, fresnel);
      float swallow = smoothstep(0.62, 0.15, noise);
      color = mix(vec3(0.002, 0.003, 0.012), uColorA * 0.22, swallow);
      color += mix(uColorB, uColorC, noise) * throat * 1.8;
      alpha = 0.78 + throat * 0.2;
    #elif STYLE == 2
      float edge = pow(fresnel, 0.6);
      float temper = 0.5 + 0.5 * sin(along * 26.0 - uTime * uFlow * 5.0 + uSeed);
      color = mix(uColorA, uColorB, temper * 0.45);
      color = mix(color, uColorC, edge);
      alpha = 0.34 + edge * 0.7;
    #elif STYLE == 3
      float caustic = pow(0.5 + 0.5 * sin(
        vWorld.x * 4.0 + snoise(vWorld * 1.8 + uTime * uFlow) * 5.0
      ), 4.0);
      color = mix(uColorA, uColorB, noise * 0.55);
      color += uColorC * (fresnel * 1.4 + caustic * 0.5);
      alpha = 0.1 + fresnel * 0.55 + caustic * 0.12;
    #elif STYLE == 4
      float furnace = 0.65 + 0.35 * sin(vWorld.y * 8.0 - uTime * uFlow * 6.0 + uSeed);
      color = mix(uColorA, uColorB, furnace);
      color = mix(color, uColorC, fresnel);
      alpha = 0.35 + fresnel * 0.8 + uPulse * 0.16;
    #else
      float bands = 0.5 + 0.5 * sin(along * 38.0 - uTime * uFlow * 8.0 + uSeed * 3.0);
      float core = pow(max(0.0, 1.0 - abs(vUv.x - 0.5) * 2.0), 2.0);
      color = mix(uColorA, uColorB, bands * 0.45);
      color = mix(color, uColorC, core * 0.8 + fresnel * 0.45);
      alpha = 0.3 + core * 0.65 + fresnel * 0.35;
    #endif

    float revealEdge = smoothstep(along - 0.08, along + 0.02, uReveal);
    alpha *= revealEdge * uFade * uOpacity;
    if (alpha < 0.004) discard;

    color *= uIntensity * uGlobalGlow * (0.8 + uPulse * 0.35);
    gl_FragColor = vec4(color, alpha);
  }
`;

const GLYPH_VERTEX = /* glsl */ `
  varying vec2 vUv;
  void main() {
    vUv = uv;
    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  }
`;

const GLYPH_FRAGMENT = /* glsl */ `
  uniform float uTime;
  uniform float uReveal;
  uniform float uFade;
  uniform float uPulse;
  uniform float uIntensity;
  uniform float uFlow;
  uniform float uSeed;
  uniform float uOpacity;
  uniform float uSweep;
  uniform float uArc;
  uniform float uGlobalGlow;
  uniform vec3 uColorA;
  uniform vec3 uColorB;
  uniform vec3 uColorC;
  varying vec2 vUv;

  ${noiseGLSL}

  float band(float d, float width) {
    return exp(-pow(d / max(width, 0.001), 2.0));
  }

  float angleDistance(float a, float b) {
    return abs(atan(sin(a - b), cos(a - b)));
  }

  void main() {
    vec2 q = (vUv - 0.5) * 2.0;
    float r = length(q);
    float a = atan(q.y, q.x);
    if (r > 1.02) discard;

    float alpha = 0.0;
    float hot = 0.0;
    float grain = 0.7 + 0.3 * snoise(vec3(q * 4.0, uSeed + uTime * uFlow * 0.25));

    #if GLYPH == 0
      // Three sides and three medians: a refractive triangle, not a circle AoE.
      const float k = 1.7320508;
      vec2 p = q;
      p.x = abs(p.x);
      float tri = max(p.x * 0.866025 + p.y * 0.5, -p.y) - 0.48;
      float outline = band(abs(tri), 0.035);
      float spokes = band(abs(q.x), 0.025) + band(abs(q.x * 0.5 + q.y * 0.866), 0.025)
        + band(abs(q.x * 0.5 - q.y * 0.866), 0.025);
      alpha = outline * 1.4 + spokes * 0.3 * smoothstep(0.15, 0.8, r);
      hot = outline;
    #elif GLYPH == 1
      float horizon = band(abs(r - 0.43 - 0.035 * sin(a * 5.0 + uTime * uFlow)), 0.025);
      float disk = band(abs(q.y * 0.35 + sin(a * 3.0 + uTime) * 0.018), 0.05)
        * smoothstep(0.15, 0.8, r);
      float spiral = band(abs(fract(a / 6.28318 * 3.0 - r * 2.2 + uTime * uFlow * 0.25) - 0.5), 0.09);
      alpha = horizon * 1.7 + disk * 0.85 + spiral * 0.22;
      hot = horizon;
    #elif GLYPH == 2
      float cut = band(abs(q.y), 0.06) * smoothstep(1.0, 0.7, abs(q.x));
      float wake = band(abs(q.y + sin(q.x * 7.0 + uSeed) * 0.025), 0.16) * 0.25;
      alpha = cut * 1.5 + wake;
      hot = cut;
    #elif GLYPH == 3
      float rim = band(abs(r - 0.76), 0.035);
      float rings = band(abs(fract(r * 5.0 - uTime * uFlow * 0.25) - 0.5), 0.11) * 0.35;
      float star = pow(max(0.0, cos(a * 6.0 + uTime * 0.4)), 18.0) * smoothstep(0.25, 0.72, r);
      alpha = rim * 1.35 + rings + star * 0.35;
      hot = rim;
    #else
      float rim = band(abs(r - 0.72), 0.035);
      float sweep = band(angleDistance(a, uSweep), max(0.025, uArc)) * smoothstep(0.08, 0.75, r);
      float counter = band(angleDistance(a, uSweep + 3.14159), 0.035) * smoothstep(0.2, 0.72, r) * 0.25;
      float ticks = pow(max(0.0, cos(a * 12.0)), 22.0) * band(abs(r - 0.72), 0.12);
      alpha = rim + sweep * 1.25 + counter + ticks * 0.55;
      hot = sweep;
    #endif

    alpha *= grain * uReveal * uFade * uOpacity;
    if (alpha < 0.004) discard;
    vec3 color = mix(uColorA, uColorB, clamp(r, 0.0, 1.0));
    color = mix(color, uColorC, clamp(hot + uPulse * 0.2, 0.0, 1.0));
    color *= uIntensity * uGlobalGlow;
    gl_FragColor = vec4(color, alpha);
  }
`;

function uniforms() {
  return sharedUniforms({
    uReveal: { value: 1 },
    uFade: { value: 1 },
    uPulse: { value: 0 },
    uIntensity: { value: 1 },
    uFlow: { value: 1 },
    uSeed: { value: 0 },
    uOpacity: { value: 1 },
    uSweep: { value: 0 },
    uArc: { value: 0.08 },
    uColorA: { value: new Color('#55ccff') },
    uColorB: { value: new Color('#b06cff') },
    uColorC: { value: new Color('#ffffff') }
  });
}

export function createConstructMaterial(style, { normal = false } = {}) {
  return new ShaderMaterial({
    defines: { STYLE: style },
    transparent: true,
    depthWrite: style === ConstructStyle.VOID,
    depthTest: true,
    blending: normal ? NormalBlending : AdditiveBlending,
    side: DoubleSide,
    toneMapped: false,
    uniforms: uniforms(),
    vertexShader: SURFACE_VERTEX,
    fragmentShader: SURFACE_FRAGMENT
  });
}

export function createConstructGlyphMaterial(glyph) {
  return new ShaderMaterial({
    defines: { GLYPH: glyph },
    transparent: true,
    depthWrite: false,
    depthTest: true,
    blending: AdditiveBlending,
    side: DoubleSide,
    toneMapped: false,
    uniforms: uniforms(),
    vertexShader: GLYPH_VERTEX,
    fragmentShader: GLYPH_FRAGMENT
  });
}

/** Push a live settings block into either construct material. */
export function syncConstructMaterial(material, config, state = {}) {
  const u = material.uniforms;
  u.uReveal.value = state.reveal ?? 1;
  u.uFade.value = state.fade ?? 1;
  u.uPulse.value = state.pulse ?? 0;
  u.uIntensity.value = config.shaderIntensity ?? 1;
  u.uFlow.value = config.flow ?? 1;
  u.uOpacity.value = config.opacity ?? 1;
  u.uSeed.value = state.seed ?? 0;
  if (u.uSweep) u.uSweep.value = state.sweep ?? 0;
  if (u.uArc) u.uArc.value = state.arc ?? 0.08;
  u.uColorA.value.set(config.colorA);
  u.uColorB.value.set(config.colorB);
  u.uColorC.value.set(config.colorC);
}
